import React from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
type Plan = {
  id: string;
  label: string;
  name: string;
  price: string;
  priceRange?: boolean;
  originalPrice?: string;
  oldValue?: string;
  priceNote: string;
  promoBadge?: string;
  description: string;
  items: string[];
  cta: {
    label: string;
    href: string;
  };
  urgency?: string;
  highlighted?: boolean;
  badge?: string;
};
const getPlans = (lang: 'es' | 'en'): {
  plan1: Plan;
  plan2: Plan;
  plan3: Plan;
} => ({
  plan1: {
    id: 'plan-1',
    label: 'PLAN 1',
    name: lang === 'es' ? 'Creación de Empresa en EE.UU.' : 'US Company Creation',
    price: '$250',
    priceNote: lang === 'es' ? 'pago único · * no incluye fee del estado' : 'one-time payment · * state fee not included',
    description: lang === 'es' ? 'Crea tu empresa en EE. UU. con claridad, estructura y acompañamiento desde el inicio.' : 'Create your US company with clarity, structure, and guidance from the start.',
    items: lang === 'es' ? ['LLC lista y activa', 'EIN listo para operar', 'Sesión estratégica 1:1', 'Guía con próximos pasos claros'] : ['LLC ready and active', 'EIN ready to operate', '1:1 Strategy session', 'Guide with clear next steps'],
    cta: {
      label: lang === 'es' ? 'Quiero crear mi empresa' : 'I want to create my company',
      href: 'https://inkovacreation.store/shop/7f390a19-9be8-4bef-be02-00a79bcf09f8'
    }
  },
  plan2: {
    id: 'plan-2',
    label: 'PLAN 2',
    name: lang === 'es' ? 'Presencia Digital Profesional' : 'Professional Digital Presence',
    price: '$500 – $1,800',
    priceRange: true,
    priceNote: lang === 'es' ? 'pago único · disponible en 2 pagos' : 'one-time payment · available in 2 installments',
    description: lang === 'es' ? 'Presenta tu negocio con una landing page profesional, clara y lista para generar confianza.' : 'Present your business with a professional landing page, clear and ready to build trust.',
    items: lang === 'es' ? ['Landing page profesional', 'Copy estratégico', 'Diseño visual alineado a tu negocio', 'Dominio personalizado', 'Botón directo a WhatsApp y canales clave'] : ['Professional landing page', 'Strategic copy', 'Visual design aligned with your business', 'Custom domain', 'Direct button to WhatsApp and key channels'],
    cta: {
      label: lang === 'es' ? 'Quiero mi presencia digital' : 'I want my digital presence',
      href: 'https://inkovacreation.store/shop/0a4a199b-ffee-460f-99fb-e38d1c6da17d'
    }
  },
  plan3: {
    id: 'plan-3',
    label: 'PLAN 3',
    name: 'Inkova Full Creation',
    price: '$1,300',
    oldValue: lang === 'es' ? 'Valor de mercado: $2,800+' : 'Market value: $2,800+',
    priceNote: lang === 'es' ? 'disponible en 2 pagos · no incluye fee del estado' : 'available in 2 installments · state fee not included',
    description: lang === 'es' ? 'Lanza tu negocio con empresa creada, presencia digital y una base profesional desde el día uno.' : 'Launch your business with a created company, digital presence, and a professional foundation from day one.',
    items: lang === 'es' ? ['LLC registrada', 'EIN listo para operar', 'Landing page profesional de 1 página', 'Dominio personalizado', 'Email corporativo profesional', 'Llamada estratégica personalizada', 'Acompañamiento guiado en cada paso'] : ['Registered LLC', 'EIN ready to operate', '1-page professional landing page', 'Custom domain', 'Professional corporate email', 'Personalized strategy call', 'Guided support every step of the way'],
    cta: {
      label: lang === 'es' ? 'Quiero el Plan Full Creation' : 'I want the Full Creation Plan',
      href: 'https://inkovacreation.store/shop/c4aa0400-b53a-4a2b-a3b1-b8dc6c49dc79'
    },
    urgency: lang === 'es' ? '⚡ Últimos cupos disponibles este mes' : '⚡ Last spots available this month',
    highlighted: true,
    badge: lang === 'es' ? 'MÁS COMPLETO' : 'MOST COMPLETE'
  }
});
function BaseCard({
  plan,
  delay
}: {
  plan: Plan;
  delay: number;
}) {
  return <motion.div id={plan.id} initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay,
    duration: 0.5
  }} whileHover={{
    y: -4
  }} className="flex flex-col h-full scroll-mt-24 self-center" style={{
    background: '#FFFFFF',
    border: '1.5px solid rgba(90,69,121,0.12)',
    borderRadius: '24px',
    padding: '40px 32px'
  }} data-id="element-421">
      <p style={{
      color: '#5a4579',
      fontSize: '11px',
      letterSpacing: '1.5px',
      fontWeight: 700
    }} data-id="element-422">
        {plan.label}
      </p>
      <h3 style={{
      fontSize: '22px',
      fontWeight: 800,
      color: '#2D1B4E',
      marginTop: '8px',
      lineHeight: 1.25
    }} data-id="element-423">
        {plan.name}
      </h3>

      {plan.promoBadge && <span className="inline-block self-start" style={{
      background: 'rgba(90,69,121,0.1)',
      color: '#5a4579',
      border: '1px solid rgba(90,69,121,0.2)',
      fontSize: '12px',
      fontWeight: 600,
      padding: '4px 12px',
      borderRadius: '20px',
      marginTop: '14px'
    }} data-id="element-424">
          {plan.promoBadge}
        </span>}

      {plan.priceRange && <p style={{
      color: '#5a4579',
      fontSize: '12px',
      fontWeight: 700,
      letterSpacing: '0.5px',
      margin: '20px 0 2px',
      textTransform: 'uppercase'
    }} data-id="element-425">
          {useLanguage().lang === 'es' ? 'Inversión personalizada' : 'Custom investment'}
        </p>}
      <div className="flex items-baseline" style={{
      gap: '10px',
      margin: plan.priceRange ? '4px 0 4px' : '20px 0 4px',
      flexWrap: 'wrap'
    }} data-id="element-426">
        <div style={{
        fontSize: plan.priceRange ? '34px' : '48px',
        fontWeight: 800,
        color: '#2D1B4E',
        lineHeight: 1.05,
        whiteSpace: 'nowrap'
      }} data-id="element-427">
          {plan.price}
        </div>
        {plan.originalPrice && <div style={{
        fontSize: '20px',
        fontWeight: 600,
        color: '#9CA3AF',
        textDecoration: 'line-through',
        lineHeight: 1.05
      }} data-id="element-428">
            {plan.originalPrice}
          </div>}
      </div>
      <div style={{
      color: '#9CA3AF',
      fontSize: '13px'
    }} data-id="element-429">
        {plan.priceNote}
      </div>

      <div style={{
      height: '1px',
      background: 'rgba(90,69,121,0.12)',
      margin: '20px 0'
    }} data-id="element-430" />

      <p style={{
      color: '#6B7280',
      fontSize: '15px',
      lineHeight: 1.6,
      marginBottom: '24px'
    }} data-id="element-431">
        {plan.description}
      </p>

      <div className="flex-grow" data-id="element-432">
        <p style={{
        color: '#5a4579',
        fontSize: '11px',
        letterSpacing: '1px',
        fontWeight: 700,
        marginBottom: '12px'
      }} data-id="element-433">
          {useLanguage().lang === 'es' ? 'INCLUYE' : 'INCLUDES'}
        </p>
        <ul className="space-y-2.5" data-id="element-434">
          {plan.items.map((item, i) => <li key={i} className="flex items-start" style={{
          gap: '10px'
        }} data-id="element-435">
              <span className="flex items-center justify-center flex-shrink-0" style={{
            width: '18px',
            height: '18px',
            borderRadius: '50%',
            background: 'rgba(90,69,121,0.1)',
            color: '#5a4579',
            marginTop: '2px'
          }} data-id="element-436">
                <Check className="w-2.5 h-2.5" strokeWidth={3} data-id="element-437" />
              </span>
              <span style={{
            color: '#374151',
            fontSize: '14px',
            lineHeight: 1.5
          }} data-id="element-438">
                {item}
              </span>
            </li>)}
        </ul>
      </div>

      <a href={plan.cta.href} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-center gap-2 transition-all duration-300" style={{
      width: '100%',
      padding: '14px',
      border: '2px solid #5a4579',
      background: 'transparent',
      color: '#5a4579',
      borderRadius: '12px',
      fontSize: '15px',
      fontWeight: 700,
      marginTop: '32px'
    }} onMouseEnter={e => {
      const el = e.currentTarget as HTMLAnchorElement;
      el.style.background = '#5a4579';
      el.style.color = '#FFFFFF';
    }} onMouseLeave={e => {
      const el = e.currentTarget as HTMLAnchorElement;
      el.style.background = 'transparent';
      el.style.color = '#5a4579';
    }} data-id="element-439">
        <span data-id="element-440">{plan.cta.label}</span>
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-441" />
      </a>
    </motion.div>;
}
function HighlightedCard({
  plan,
  delay
}: {
  plan: Plan;
  delay: number;
}) {
  return <motion.div id={plan.id} initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay,
    duration: 0.6
  }} className="relative overflow-hidden flex flex-col h-full scroll-mt-24 lg:scale-[1.05]" style={{
    background: 'linear-gradient(160deg, #4a3666 0%, #5a4579 40%, #8B6BA8 100%)',
    border: 'none',
    borderRadius: '28px',
    padding: '48px 36px',
    boxShadow: '0 32px 80px rgba(90,69,121,0.4)'
  }} data-id="element-442">
      {/* Decorative orbs */}
      <div className="absolute pointer-events-none" style={{
      top: '-60px',
      right: '-60px',
      width: '200px',
      height: '200px',
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.05)'
    }} aria-hidden="true" data-id="element-443" />
      <div className="absolute pointer-events-none" style={{
      bottom: '-40px',
      left: '-40px',
      width: '150px',
      height: '150px',
      borderRadius: '50%',
      background: 'rgba(255,255,255,0.04)'
    }} aria-hidden="true" data-id="element-444" />

      {/* Badge MÁS COMPLETO */}
      {plan.badge && <span className="absolute" style={{
      top: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#FFFFFF',
      color: '#5a4579',
      fontSize: '11px',
      fontWeight: 800,
      padding: '6px 20px',
      borderRadius: '0 0 16px 16px',
      letterSpacing: '1px',
      whiteSpace: 'nowrap'
    }} data-id="element-445">
          {plan.badge}
        </span>}

      <div className="relative" data-id="element-446">
        <p style={{
        color: 'rgba(255,255,255,0.6)',
        fontSize: '11px',
        letterSpacing: '1.5px',
        fontWeight: 700,
        marginTop: '20px'
      }} data-id="element-447">
          {plan.label}
        </p>
        <h3 style={{
        color: '#FFFFFF',
        fontSize: '26px',
        fontWeight: 800,
        marginTop: '6px',
        lineHeight: 1.2
      }} data-id="element-448">
          {plan.name}
        </h3>

        {plan.promoBadge && <span className="inline-block" style={{
        background: 'rgba(255,255,255,0.15)',
        border: '1px solid rgba(255,255,255,0.25)',
        color: '#FFFFFF',
        fontSize: '12px',
        fontWeight: 600,
        padding: '4px 12px',
        borderRadius: '20px',
        margin: '12px 0'
      }} data-id="element-449">
            {plan.promoBadge}
          </span>}

        {plan.oldValue && <p style={{
        color: 'rgba(255,255,255,0.5)',
        fontSize: '12px',
        textDecoration: 'line-through',
        marginTop: '4px'
      }} data-id="element-450">
            {plan.oldValue}
          </p>}

        <div className="flex items-baseline" style={{
        gap: '12px',
        marginTop: '4px',
        flexWrap: 'wrap'
      }} data-id="element-451">
          <div style={{
          fontSize: '56px',
          fontWeight: 900,
          color: '#FFFFFF',
          lineHeight: 1.05
        }} data-id="element-452">
            {plan.price}
          </div>
          {plan.originalPrice && <div style={{
          fontSize: '22px',
          fontWeight: 600,
          color: 'rgba(255,255,255,0.5)',
          textDecoration: 'line-through',
          lineHeight: 1.05
        }} data-id="element-453">
              {plan.originalPrice}
            </div>}
        </div>
        <div style={{
        color: 'rgba(255,255,255,0.6)',
        fontSize: '13px'
      }} data-id="element-454">
          {plan.priceNote}
        </div>

        <div style={{
        height: '1px',
        background: 'rgba(255,255,255,0.15)',
        margin: '20px 0'
      }} data-id="element-455" />

        <p style={{
        color: 'rgba(255,255,255,0.8)',
        fontSize: '15px',
        lineHeight: 1.6,
        marginBottom: '24px'
      }} data-id="element-456">
          {plan.description}
        </p>

        <p style={{
        color: 'rgba(255,255,255,0.5)',
        fontSize: '11px',
        letterSpacing: '1px',
        fontWeight: 700,
        marginBottom: '12px'
      }} data-id="element-457">
          {useLanguage().lang === 'es' ? 'INCLUYE' : 'INCLUDES'}
        </p>
        <ul className="space-y-2.5" data-id="element-458">
          {plan.items.map((item, i) => <li key={i} className="flex items-start" style={{
          gap: '10px'
        }} data-id="element-459">
              <span className="flex items-center justify-center flex-shrink-0" style={{
            width: '18px',
            height: '18px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            color: '#FFFFFF',
            marginTop: '2px'
          }} data-id="element-460">
                <Check className="w-2.5 h-2.5" strokeWidth={3} data-id="element-461" />
              </span>
              <span style={{
            color: '#FFFFFF',
            fontSize: '14px',
            lineHeight: 1.5
          }} data-id="element-462">
                {item}
              </span>
            </li>)}
        </ul>

        <a href={plan.cta.href} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-center gap-2 transition-all duration-300" style={{
        width: '100%',
        padding: '16px',
        background: '#FFFFFF',
        color: '#5a4579',
        borderRadius: '12px',
        fontSize: '16px',
        fontWeight: 800,
        marginTop: '32px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
      }} onMouseEnter={e => {
        ;
        (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(255,255,255,0.9)';
      }} onMouseLeave={e => {
        ;
        (e.currentTarget as HTMLAnchorElement).style.background = '#FFFFFF';
      }} data-id="element-463">
          <span data-id="element-464">{plan.cta.label}</span>
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-465" />
        </a>

        {plan.urgency && <p style={{
        color: 'rgba(255,255,255,0.6)',
        fontSize: '12px',
        textAlign: 'center',
        marginTop: '12px'
      }} data-id="element-466">
            {plan.urgency}
          </p>}
      </div>
    </motion.div>;
}
export function ServicesSection() {
  const {
    lang
  } = useLanguage();
  const {
    plan1,
    plan2,
    plan3
  } = getPlans(lang);
  return <section id="planes" style={{
    background: '#FFFFFF',
    padding: '100px 0 60px'
  }} data-id="element-467">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-468">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto" data-id="element-469">
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} style={{
          color: '#5a4579',
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '2px',
          textTransform: 'uppercase',
          textAlign: 'center'
        }} data-id="element-470">
            {lang === 'es' ? 'Nuestros planes' : 'Our plans'}
          </motion.p>
          <motion.h2 initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.05
        }} style={{
          fontSize: '38px',
          fontWeight: 800,
          color: '#2D1B4E',
          textAlign: 'center',
          marginTop: '8px',
          lineHeight: 1.2
        }} data-id="element-471">
            {lang === 'es' ? 'Elige el plan que más se adapta a donde estás hoy' : 'Choose the plan that best fits where you are today'}
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} style={{
          color: '#6B7280',
          fontSize: '16px',
          textAlign: 'center',
          maxWidth: '520px',
          margin: '12px auto 64px',
          lineHeight: 1.6
        }} data-id="element-472">
            {lang === 'es' ? 'Cada plan fue diseñado para una etapa específica. Sin relleno, sin extras que no necesitas.' : 'Each plan was designed for a specific stage. No filler, no extras you do not need.'}
          </motion.p>
        </div>

        {/* Plans grid — Plan 3 first on mobile, centered on desktop */}
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-6 mb-20 items-stretch" data-id="element-473">
          {/* Plan 1: desktop col 1, mobile order 2 */}
          <div className="order-2 lg:order-1" data-id="element-474">
            <BaseCard plan={plan1} delay={0.1} data-id="element-475" />
          </div>
          {/* Plan 3: desktop col 2 (center), mobile order 1 */}
          <div className="order-1 lg:order-2" data-id="element-476">
            <HighlightedCard plan={plan3} delay={0} data-id="element-477" />
          </div>
          {/* Plan 2: desktop col 3, mobile order 3 */}
          <div className="order-3 lg:order-3" data-id="element-478">
            <BaseCard plan={plan2} delay={0.2} data-id="element-479" />
          </div>
        </div>

        {/* Digital Upgrade Section */}
        <motion.div id="digital-upgrade" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="scroll-mt-24 mb-20" style={{
        background: '#f7f4fb',
        border: '1.5px solid rgba(90,69,121,0.15)',
        borderRadius: '28px',
        padding: '48px',
        position: 'relative',
        overflow: 'hidden'
      }} data-id="element-480">
          {/* Decorative element */}
          <div aria-hidden="true" style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(90,69,121,0.08) 0%, rgba(90,69,121,0) 70%)',
          transform: 'translate(30%, -30%)',
          borderRadius: '50%',
          pointerEvents: 'none'
        }} data-id="element-481" />

          <div className="grid md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-482">
            <div data-id="element-483">
              <span style={{
              display: 'inline-block',
              background: 'rgba(90,69,121,0.1)',
              color: '#5a4579',
              padding: '6px 16px',
              borderRadius: '99px',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '1px',
              marginBottom: '20px'
            }} data-id="element-484">
                {lang === 'es' ? 'NUEVO SERVICIO' : 'NEW SERVICE'}
              </span>
              <h3 style={{
              fontSize: 'clamp(28px, 4vw, 36px)',
              fontWeight: 800,
              color: '#1a1020',
              lineHeight: 1.2,
              marginBottom: '16px'
            }} data-id="element-485">
                Digital Upgrade
              </h3>
              <p style={{
              fontSize: '16px',
              color: '#6b5f78',
              lineHeight: 1.6,
              marginBottom: '32px'
            }} data-id="element-486">
                {lang === 'es' ? 'Para negocios que ya existen pero necesitan una mejora digital. Llevamos tu presencia online al siguiente nivel para que refleje la verdadera calidad de tu empresa.' : 'For existing businesses that need a digital improvement. We take your online presence to the next level so it reflects the true quality of your company.'}
              </p>

              <a href="https://inkovacreation.store/shop/341dc5f2-2e37-4bec-89a7-31063f77eefd" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 transition-all" style={{
              background: '#5a4579',
              color: '#FFFFFF',
              padding: '16px 32px',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 700,
              boxShadow: '0 8px 24px rgba(90,69,121,0.25)'
            }} onMouseEnter={e => {
              const el = e.currentTarget as HTMLAnchorElement;
              el.style.transform = 'translateY(-2px)';
              el.style.boxShadow = '0 12px 32px rgba(90,69,121,0.35)';
            }} onMouseLeave={e => {
              const el = e.currentTarget as HTMLAnchorElement;
              el.style.transform = 'translateY(0)';
              el.style.boxShadow = '0 8px 24px rgba(90,69,121,0.25)';
            }} data-id="element-487">
                {lang === 'es' ? 'Solicitar Upgrade' : 'Request Upgrade'}
                <ArrowRight size={18} data-id="element-488" />
              </a>
            </div>

            <div style={{
            background: '#FFFFFF',
            borderRadius: '20px',
            padding: '32px',
            boxShadow: '0 12px 40px rgba(90,69,121,0.06)'
          }} data-id="element-489">
              <p style={{
              color: '#5a4579',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '1px',
              marginBottom: '20px'
            }} data-id="element-490">
                {lang === 'es' ? 'QUÉ INCLUYE' : 'WHAT IS INCLUDED'}
              </p>
              <ul className="space-y-6" data-id="element-491">
                {[lang === 'es' ? 'Auditoría de sitio web actual' : 'Current website audit', lang === 'es' ? 'Nueva landing page profesional' : 'New professional landing page', lang === 'es' ? 'Actualización de branding corporativo' : 'Corporate branding refresh'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-492">
                    <div className="flex items-center justify-center flex-shrink-0" style={{
                  width: '28px',
                  height: '28px',
                  background: 'rgba(90,69,121,0.1)',
                  borderRadius: '8px',
                  color: '#5a4579'
                }} data-id="element-493">
                      <Check size={16} strokeWidth={3} data-id="element-494" />
                    </div>
                    <span style={{
                  fontSize: '16px',
                  color: '#1a1020',
                  fontWeight: 600,
                  lineHeight: 1.4,
                  marginTop: '2px'
                }} data-id="element-495">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Add-ons compact list */}
        <div id="servicios-adicionales" className="addons-wrap" style={{
        background: 'linear-gradient(135deg, #5a4579 0%, #4a3666 100%)',
        padding: '60px 20px',
        marginLeft: 'calc(50% - 50vw)',
        marginRight: 'calc(50% - 50vw)',
        width: '100vw',
        marginTop: '48px'
      }} data-id="element-496">
          <style data-id="element-497">{`
            @media (min-width: 769px) {
              .addons-wrap { padding: 80px 40px !important; }
            }
            .addons-inner { max-width: 760px; margin: 0 auto; }
          `}</style>
          <div className="addons-inner" data-id="element-498">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-499">
              <p style={{
              color: '#5a4579',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '2px',
              textTransform: 'uppercase',
              textAlign: 'center',
              marginBottom: '8px'
            }} data-id="element-500">
                {lang === 'es' ? 'Servicios adicionales' : 'Additional services'}
              </p>
              <h3 className="text-[24px] md:text-[28px]" style={{
              color: '#FFFFFF',
              fontWeight: 800,
              textAlign: 'center',
              marginBottom: '4px',
              lineHeight: 1.2
            }} data-id="element-501">
                {lang === 'es' ? '¿Necesitas algo más?' : 'Need something else?'}
              </h3>
              <p style={{
              color: 'rgba(255,255,255,0.65)',
              fontSize: '14px',
              textAlign: 'center',
              marginBottom: '32px'
            }} data-id="element-502">
                {lang === 'es' ? 'Agrega lo que necesites a tu plan actual' : 'Add what you need to your current plan'}
              </p>

              <div className="overflow-hidden" style={{
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '16px'
            }} data-id="element-503">
                {([{
                icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-504">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" data-id="element-505" />
                          <polyline points="14 2 14 8 20 8" data-id="element-506" />
                          <line x1="16" y1="13" x2="8" y2="13" data-id="element-507" />
                          <line x1="16" y1="17" x2="8" y2="17" data-id="element-508" />
                          <polyline points="10 9 9 9 8 9" data-id="element-509" />
                        </svg>,
                name: 'Annual Report (Sunbiz)',
                price: '$250 / año',
                href: 'https://inkovacreation.store/shop/2daa36ca-b0f2-4795-8e34-a50134ca08da'
              }, {
                icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-510">
                          <circle cx="12" cy="12" r="10" data-id="element-511" />
                          <path d="M8.56 2.75c4.37 6.03 6.02 9.42 8.03 17.72m2.54-15.38c-3.72 4.35-8.94 5.66-16.88 5.85m19.5 1.9c-3.5-.93-6.63-.82-8.94 0-2.58.92-5.01 2.86-7.44 6.32" data-id="element-512" />
                        </svg>,
                name: 'Logo profesional',
                price: '$295',
                href: 'https://inkovacreation.store/shop/d9f43c4e-29cf-4042-88ac-baa7ae4d3c46'
              }, {
                icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-513">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-514" />
                        </svg>,
                name: 'Notaría',
                price: '$15 – $25',
                href: 'https://wa.me/17863037695?text=Hola%2C%20quiero%20notariar%20un%20documento.%20%C2%BFQu%C3%A9%20necesito%20y%20cu%C3%A1l%20es%20el%20proceso%3F'
              }, {
                icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-515">
                          <path d="M18 20V10" data-id="element-516" />
                          <path d="M12 20V4" data-id="element-517" />
                          <path d="M6 20v-6" data-id="element-518" />
                        </svg>,
                name: 'Membresía Inkova Web Care',
                price: '$50 / mes',
                href: 'https://inkovacreation.store/shop/7d029968-338d-4b4b-a375-a84f6858d94b'
              }, {
                icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-519">
                          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" data-id="element-520" />
                          <line x1="8" y1="21" x2="16" y2="21" data-id="element-521" />
                          <line x1="12" y1="17" x2="12" y2="21" data-id="element-522" />
                        </svg>,
                name: 'Storefront: Setup de Beacons',
                price: '$295',
                href: 'https://inkovacreation.store/shop/f4a5e0ff-a84e-465d-8ce7-f3d7ebd35ffa'
              }] as const).map((row, idx, arr) => <div key={idx} className="flex items-center transition-colors" style={{
                gap: '12px',
                padding: '14px 20px',
                borderBottom: idx === arr.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.08)'
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.05)';
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLDivElement).style.background = 'transparent';
              }} data-id="element-523">
                    <div className="flex items-center justify-center flex-shrink-0" style={{
                  width: '36px',
                  height: '36px',
                  minWidth: '36px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '10px',
                  color: '#FFFFFF'
                }} data-id="element-524">
                      {row.icon}
                    </div>
                    <div className="flex-1 min-w-0 text-[14px] md:text-[15px]" style={{
                  fontWeight: 600,
                  color: '#FFFFFF'
                }} data-id="element-525">
                      {row.name}
                    </div>
                    <div style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#C39BD3',
                  whiteSpace: 'nowrap',
                  marginRight: '12px'
                }} data-id="element-526">
                      {row.price}
                    </div>
                    <a href={row.href} target="_blank" rel="noopener noreferrer" className="transition-all" style={{
                  background: 'transparent',
                  border: '1.5px solid rgba(255,255,255,0.3)',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  cursor: 'pointer'
                }} onMouseEnter={e => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  el.style.background = '#FFFFFF';
                  el.style.color = '#5a4579';
                  el.style.borderColor = '#FFFFFF';
                }} onMouseLeave={e => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  el.style.background = 'transparent';
                  el.style.color = '#FFFFFF';
                  el.style.borderColor = 'rgba(255,255,255,0.3)';
                }} data-id="element-527">
                      {lang === 'es' ? 'Solicitar' : 'Request'}
                    </a>
                  </div>)}
              </div>

              {/* Mobile-only price row note: prices are hidden under sm; show price inline under name */}
              <div style={{
              textAlign: 'center',
              marginTop: '20px'
            }} data-id="element-528">
                <a href="https://wa.me/17863037695?text=Hola%2C%20quiero%20m%C3%A1s%20info%20sobre%20los%20servicios%20adicionales%20de%20Inkova." target="_blank" rel="noopener noreferrer" className="inline-flex items-center transition-all" style={{
                gap: '8px',
                background: '#25D366',
                color: '#FFFFFF',
                borderRadius: '10px',
                padding: '12px 22px',
                fontSize: '14px',
                fontWeight: 700,
                boxShadow: '0 6px 20px rgba(37,211,102,0.35)'
              }} onMouseEnter={e => {
                const el = e.currentTarget as HTMLAnchorElement;
                el.style.background = '#20BD5A';
                el.style.transform = 'translateY(-2px)';
                el.style.boxShadow = '0 10px 28px rgba(37,211,102,0.45)';
              }} onMouseLeave={e => {
                const el = e.currentTarget as HTMLAnchorElement;
                el.style.background = '#25D366';
                el.style.transform = 'translateY(0)';
                el.style.boxShadow = '0 6px 20px rgba(37,211,102,0.35)';
              }} data-id="element-529">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" data-id="element-530">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" data-id="element-531" />
                  </svg>
                  {lang === 'es' ? 'Quiero más info · Hablar por WhatsApp' : 'I want more info · Chat on WhatsApp'}
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}